import {
  PhotoRecognitionAttempts,
  RecognitionAttemptCandidate,
  RecognitionFace,
} from '../../types'

// A null personId means there was no matched person at this rank. A non-null
// personId resolves to the display name fetched from the persons GetNames
// endpoint, falling back to "Person <id>" when the name map is missing that id
// (names not yet loaded, or the GetNames call failed).
export const NO_MATCH_LABEL = 'No match'

export const resolveCandidateName = (
  personId: number | null,
  nameMap: Map<number, string>,
): string => {
  if (personId === null) return NO_MATCH_LABEL
  return nameMap.get(personId) || `Person ${personId}`
}

// Characters that make a spreadsheet treat a cell as a formula. A leading tab
// or carriage return can also coerce evaluation in some tools.
const FORMULA_TRIGGERS = ['=', '+', '-', '@', '\t', '\r']

// RFC 4180 cell quoting with spreadsheet-formula-injection defense:
//   1. If the text begins with a formula trigger (= + - @ \t \r), prepend a
//      single apostrophe so Excel/Sheets render it as literal text, never a
//      formula (CSV injection guard). Applied BEFORE quoting.
//   2. Wrap every field in double quotes and double any embedded quote so
//      names/timestamps containing commas or quotes can't break the columns.
export const csvCell = (value: string | number | boolean | null): string => {
  const raw = value === null ? '' : String(value)
  const guarded =
    raw.length > 0 && FORMULA_TRIGGERS.includes(raw[0]) ? `'${raw}` : raw
  return `"${guarded.replace(/"/g, '""')}"`
}

interface CsvColumn {
  header: string
  value: (
    faceIndex: number,
    face: RecognitionFace,
    candidate: RecognitionAttemptCandidate,
    nameMap: Map<number, string>,
  ) => string | number | boolean | null
}

// Single source of truth for the CSV shape: each column pairs its header with
// the function that produces its per-candidate value, so headers and row values
// can never drift out of sync.
export const CSV_COLUMNS: CsvColumn[] = [
  {header: 'Face', value: (faceIndex) => faceIndex + 1},
  {header: 'AzureFaceId', value: (_i, face) => face.azureFaceId},
  {header: 'AttemptedUTC', value: (_i, face) => face.attemptedUTC},
  {header: 'Rank', value: (_i, _face, candidate) => candidate.rank},
  {
    header: 'PersonId',
    value: (_i, _face, candidate) => candidate.personId ?? '',
  },
  {
    header: 'Name',
    value: (_i, _face, candidate, nameMap) =>
      resolveCandidateName(candidate.personId, nameMap),
  },
  {
    header: 'Confidence',
    value: (_i, _face, candidate) => candidate.confidence ?? '',
  },
  {
    header: 'Threshold',
    value: (_i, _face, candidate) => candidate.thresholdUsed,
  },
  {
    header: 'Accepted',
    value: (_i, _face, candidate) => (candidate.accepted ? 'Yes' : 'No'),
  },
  {
    header: 'TrainedUTC',
    value: (_i, _face, candidate) => candidate.trainedUTC ?? '',
  },
]

export const buildRecognitionCsv = (
  data: PhotoRecognitionAttempts,
  nameMap: Map<number, string>,
): string => {
  const rows: string[] = [
    CSV_COLUMNS.map((column) => csvCell(column.header)).join(','),
  ]
  data.faces.forEach((face, faceIndex) => {
    face.candidates.forEach((candidate) => {
      rows.push(
        CSV_COLUMNS.map((column) =>
          csvCell(column.value(faceIndex, face, candidate, nameMap)),
        ).join(','),
      )
    })
  })
  return rows.join('\n')
}
